import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { ShieldCheck, Loader2, Copy, Check, Smartphone, QrCode, KeyRound, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { isNativeApp } from '@/utils/platform';
import { startTwoFactorSetup, confirmTwoFactor, TwoFactorSetup } from '@/services/twoFactorService';
import { useLanguage } from '@/contexts/LanguageContext';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onEnrolled?: () => void;
}

type Step = 'setup' | 'verify' | 'done';

const TwoFactorEnrollDialog = ({ open, onOpenChange, onEnrolled }: Props) => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const compact = isMobile || isNativeApp();

  const [step, setStep] = useState<Step>('setup');
  const [loading, setLoading] = useState(false);
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [showQrOnMobile, setShowQrOnMobile] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const { t } = useLanguage();

  useEffect(() => {
    if (!open) {
      setStep('setup');
      setSetup(null);
      setCode('');
      setCopied(false);
      setShowQrOnMobile(false);
      setRecoveryCodes([]);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        setSetup(await startTwoFactorSetup());
      } catch (e: any) {
        toast({ variant: 'destructive', title: 'Erreur', description: e.message });
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const copySecret = async () => {
    if (!setup) return;
    try {
      await navigator.clipboard.writeText(setup.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ variant: 'destructive', title: 'Impossible de copier la clé' });
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) return;
    setLoading(true);
    try {
      const res = await confirmTwoFactor(code);
      setRecoveryCodes(res.recovery_codes || []);
      setStep('done');
      onEnrolled?.();
    } catch (e: any) {
      setCode('');
      toast({ variant: 'destructive', title: 'Code invalide', description: e.message });
    } finally {
      setLoading(false);
    }
  };

  const secretBlock = (
    <div className="rounded-lg border bg-muted/40 p-3">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        <KeyRound className="h-3 w-3" /> {t('login.secretKey')}
      </Label>
      <div className="flex items-center gap-2 mt-2">
        <code className="flex-1 text-sm font-mono break-all select-all">{setup?.secret}</code>
        <Button size="sm" variant="outline" onClick={copySecret} type="button">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
      {setup?.account && (
        <p className="text-[11px] text-muted-foreground mt-2">
          {t('login.account')} : <b>{setup.account}</b>
          {setup.issuer ? <> · {t('login.issuer')} : <b>{setup.issuer}</b></> : null}
        </p>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-4">
        <DialogHeader>
          <div className="mx-auto h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mb-2">
            <ShieldCheck className="h-7 w-7 text-primary" />
          </div>
          <DialogTitle className="text-center text-2xl">{t('login.twoFaTitle')}</DialogTitle>
          <DialogDescription className="text-center">
            {step === 'setup' && t('login.addAccountTOAuthApp')}
            {step === 'verify' && t('login.enterCode')}
            {step === 'done' && t('login.accountProtected')}
          </DialogDescription>
        </DialogHeader>

        {loading && !setup && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {step === 'setup' && setup && (
          <div className="space-y-5 py-2">
            {!compact ? (
              <>
                <div className="flex flex-col items-center gap-3">
                  <div className="bg-white p-4 rounded-xl border">
                    <QRCodeSVG value={setup.otpauth_url} size={200} level="M" />
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <QrCode className="h-3 w-3" /> {t('login.ScanQrCode')}
                  </p>
                </div>
                {secretBlock}
              </>
            ) : (
              <>
                {secretBlock}
                
                {showQrOnMobile ? (
                  <div className="flex flex-col items-center gap-3">
                    <div className="bg-white p-4 rounded-xl border">
                      <QRCodeSVG value={setup.otpauth_url} size={180} level="M" />
                    </div>
                  </div>
                ) : (
                  <Button variant="outline" className="w-full" type="button" onClick={() => setShowQrOnMobile(true)}>
                    <QrCode className="h-4 w-4 mr-2" /> {t('login.showQrCode')}
                  </Button>
                )}
              </>
            )}

            <Button className="w-full h-11" onClick={() => setStep('verify')}>
              {t('login.iAddedAccount')}
            </Button>
          </div>
        )}

        {step === 'verify' && (
          <div className="flex flex-col items-center gap-5 py-2">
            <InputOTP maxLength={6} value={code} onChange={((v: string) => setCode(v)) as never} disabled={loading} autoFocus>
              <InputOTPGroup className="gap-2">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <InputOTPSlot key={i} index={i} className="h-14 w-11 text-2xl font-bold rounded-lg border-2 first:rounded-l-lg last:rounded-r-lg" />
                ))}
              </InputOTPGroup>
            </InputOTP>
            <Button className="w-full h-11" onClick={handleVerify} disabled={loading || code.length !== 6}>
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('login.enableTwoFa')}
            </Button>
            <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setStep('setup')}>
              ← {t('login.seeAgainKey')}
            </button>
          </div>
        )}

        {step === 'done' && (
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="h-16 w-16 rounded-full bg-green-500/15 flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <p className="text-center text-sm text-muted-foreground">
              {t('login.atNextCOnnection')}
            </p>
            {recoveryCodes.length > 0 && (
              <div className="w-full rounded-lg border bg-muted/40 p-3">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Codes de secours</Label>
                <div className="grid grid-cols-2 gap-1 mt-2 font-mono text-xs">
                  {recoveryCodes.map((c) => <span key={c} className="select-all">{c}</span>)}
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">{t('login.saveRecoveryCodes')}</p>
              </div>
            )}
            <Button className="w-full" onClick={() => onOpenChange(false)}>
              {t('login.done')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorEnrollDialog;
